/**
 * Study sync — shared planning-state persistence and automatic study saving.
 *
 * Planning state (intervention selections etc.) lives in browser localStorage;
 * it is collected into each saved study and written back when one is opened.
 *
 * Auto-save flow: when the upload pipeline finishes processing, UploadPage
 * marks a pending auto-save in sessionStorage. The Measurements page consumes
 * the mark — immediately for scans with no CT measurements pipeline, or as
 * soon as the measurement pipeline reports completion — and saves the study
 * without any user action. Opening a saved study never sets the mark, so
 * restores are never re-saved as duplicates.
 */

import { api } from './api';

// Keys mirror what the Measurements/Planning/Parameters pages keep in
// localStorage via useLocalStorage.
export const PLAN_KEYS = [
  'anatomical-selected-rows',
  'anatomical-concerning-rows',
  'anatomical-herniated-rows',
  'vertebral-interventions',
  'intervertebral-interventions',
  'interbody-device-selections',
  'cage-selections',
  'pedicle-screw-selections',
  'plate-screw-lengths',
  'proposed-parameters',
  'parameters-selected-rows',
  'parameters-concerning-rows'
];

export const collectPlanningState = () => {
  const state = {};
  PLAN_KEYS.forEach(key => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw !== null) state[key] = JSON.parse(raw);
    } catch (e) {
      // Unreadable entry — skip it rather than fail the save
    }
  });
  return state;
};

/** Wipe every planning-state key plus the cached CT measurements. UploadPage
 *  calls this on a new scan so nothing from the previous case survives into
 *  the next auto-saved study — the key list must never be duplicated at call
 *  sites, or it drifts when a key is added here. */
export const clearPlanningState = () => {
  PLAN_KEYS.forEach(key => window.localStorage.removeItem(key));
  window.sessionStorage.removeItem('ctMeasurements');
};

export const applyPlanningState = (interventions) => {
  // Clear the previous case's planning state, then load the saved one
  PLAN_KEYS.forEach(key => window.localStorage.removeItem(key));
  window.sessionStorage.removeItem('ctMeasurements');
  // Opening a saved study must never look like a fresh processing run — kill
  // any auto-save mark a still-running scan may have left behind.
  clearAutoSavePending();
  if (interventions) {
    Object.entries(interventions).forEach(([key, value]) => {
      window.localStorage.setItem(key, JSON.stringify(value));
    });
  }
};

const AUTOSAVE_FLAG_KEY = 'armita-autosave-pending';

/** Called by UploadPage when processing finishes and produced results.
 *  awaitMeasurements: true when the CT measurement pipeline will still run —
 *  the save then waits for it so measurements are part of the study. */
export const markAutoSavePending = ({ awaitMeasurements }) => {
  try {
    window.sessionStorage.setItem(
      AUTOSAVE_FLAG_KEY,
      JSON.stringify({ awaitMeasurements, markedAt: Date.now() })
    );
  } catch (e) {
    console.warn('Could not mark auto-save:', e);
  }
};

export const peekAutoSavePending = () => {
  try {
    const raw = window.sessionStorage.getItem(AUTOSAVE_FLAG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const clearAutoSavePending = () => {
  window.sessionStorage.removeItem(AUTOSAVE_FLAG_KEY);
};

const autoSaveName = () => {
  const now = new Date();
  const date = now.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric'
  });
  const time = now.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
  return `Scan ${date} ${time}`;
};

/**
 * Save the current scan as a study, silently tolerating deployments where
 * studies are unavailable (no database) or the user lacks the role.
 *
 * Returns { saved: true, message } on success,
 *         { saved: false, unsupported: true } when saving isn't available,
 *         { saved: false, error } on a real failure worth surfacing.
 */
export const autoSaveStudy = async () => {
  try {
    const response = await api.post('/studies', {
      name: autoSaveName(),
      description: 'Saved automatically when processing completed',
      interventions: collectPlanningState()
    });
    return { saved: true, message: response.data?.message };
  } catch (error) {
    const status = error.response?.status;
    // 404: studies API not mounted (file-auth deployment). 403: viewer role.
    // 400: nothing to save. None of these should alarm the user.
    if (status === 404 || status === 403 || status === 400) {
      console.warn('Auto-save unavailable:', status, error.response?.data?.detail);
      return { saved: false, unsupported: true };
    }
    console.error('Auto-save failed:', error);
    return { saved: false, error: error.message || 'Auto-save failed' };
  }
};
